import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import StatCard from '../ui/StatCard';
import GlassCard from '../ui/GlassCard';
import Button from '../ui/Button';
import { FiClock, FiTarget, FiTrendingUp, FiAward, FiZap, FiActivity } from 'react-icons/fi';

/**
 * Dashboard bento grid for logged-in users.
 * Apple Health inspired layout with stat cards.
 */
export default function DashboardBento({
  userId, username, examType, target, daysLeft, examDate,
  streak, xp, level, dailyQuote,
  onSetDate, onShareCard, onSetTarget
}) {
  const navigate = useNavigate();
  const [latestTrial, setLatestTrial] = useState(null);

  useEffect(() => {
    if (!userId) return;
    const fetchLatestTrial = async () => {
      try {
        const q = query(collection(db, "denemeler"), where("userId", "==", userId));
        const snap = await getDocs(q);
        const entries = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (entries.length > 0) {
          entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          setLatestTrial(entries[0]);
        }
      } catch (err) {
        console.error("Failed to fetch latest trial:", err);
      }
    };
    fetchLatestTrial();
  }, [userId]);

  const getMotivationalMessage = (s) => {
    if (s >= 30) return '30 gün! Efsane bir streak! ✨';
    if (s >= 15) return '15 gün streak! Savaşçısın! 🥇';
    if (s >= 7) return 'Bir hafta, durdurulamazsın! 🚀';
    if (s >= 3) return '3 gündür harikasın! 🌟';
    if (s === 1) return 'Harika başlangıç! 💪';
    return 'Bugün streak başlat! 🔥';
  };

  return (
    <div className="dashboard-bento">
      {/* Welcome */}
      <motion.div
        className="dashboard-welcome"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="dashboard-greeting">
          Hoş geldin, <span className="text-gradient">{username}</span>
        </h1>
        <p className="text-secondary">Bugün hedeflerine bir adım daha yaklaşma zamanı.</p>
      </motion.div>

      {/* Seamless Giant Clock Timer */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        style={{
          width: '100%',
          marginBottom: 'var(--space-8)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        <div style={{ zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', width: '100%' }}>
          <div style={{ 
            fontFamily: 'var(--font-mono)', 
            fontWeight: 200, 
            fontSize: 'clamp(4rem, 16vw, 12rem)', 
            color: 'var(--text-primary)', 
            letterSpacing: '-0.02em', 
            display: 'flex', 
            alignItems: 'baseline', 
            justifyContent: 'center',
            textShadow: '0 0 60px rgba(16, 185, 129, 0.2)',
            marginBottom: 'var(--space-4)',
            width: '100%',
            lineHeight: 1
          }}>
            <span>00:00:00</span>
            <span style={{ fontSize: 'clamp(2rem, 8vw, 5rem)', marginLeft: '8px', color: 'var(--text-muted)' }}>.0</span>
          </div>
          
          <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-5)', maxWidth: '400px', fontSize: 'var(--text-lg)' }}>
            Odaklanmaya hazır mısın? Kronometreni başlat ve hedeflerine doğru bir adım daha at.
          </p>
          
          <Button 
            variant="primary" 
            size="xl" 
            onClick={() => navigate('/study-time', { state: { autoStart: true } })} 
            icon={<FiZap size={22} />}
            style={{ 
              fontSize: '1.2rem', padding: '16px 40px', borderRadius: 'var(--radius-full)',
              background: 'linear-gradient(90deg, #10b981, #059669)',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)'
            }}
          >
            Hemen Başla
          </Button>
        </div>
      </motion.div>

      {/* Stats Row */}
      <div className="bento-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <div onClick={onSetDate} style={{ cursor: 'pointer' }}>
          <StatCard
            value={`${daysLeft}`}
            label={`${examType} • Kalan Gün`}
            icon={<FiTarget size={20} />}
            accentColor="var(--color-warning)"
          >
             <p className="text-xs text-muted" style={{ marginTop: 'var(--space-1)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                ✏️ Tarihi Değiştir
             </p>
          </StatCard>
        </div>

        <StatCard
          value={streak}
          label="Streak"
          icon={<FiZap size={20} />}
          accentColor="var(--color-danger)"
        >
          <p className="text-xs text-muted" style={{ marginTop: 'var(--space-1)' }}>
            {getMotivationalMessage(streak)}
          </p>
        </StatCard>

        <StatCard
          value={level}
          label="Seviye"
          icon={<FiTrendingUp size={20} />}
          accentColor="var(--accent-primary)"
        >
          <div style={{ marginTop: 'var(--space-2)', width: '100%', height: 4, background: 'var(--border-subtle)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(xp % 100)}%`, background: 'var(--accent-gradient)', borderRadius: 2, transition: 'width 0.3s ease' }} />
          </div>
          <p className="text-xs text-muted">{xp % 100}/100 XP</p>
        </StatCard>

        <div onClick={onSetTarget} style={{ cursor: 'pointer' }}>
          <StatCard
            value={`${target || '—'}`}
            label="Hedef"
            icon={<FiAward size={20} />}
            accentColor="var(--accent-secondary)"
          >
             <p className="text-xs text-muted" style={{ marginTop: 'var(--space-1)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                ✏️ Hedefi Değiştir
             </p>
          </StatCard>
        </div>

        <div onClick={() => navigate('/trial')} style={{ cursor: 'pointer' }}>
          <StatCard
            value={latestTrial ? `${latestTrial.toplamNet} N` : '—'}
            label={latestTrial ? `${latestTrial.type === 'genel' ? 'Genel' : 'Branş'} Deneme` : 'Deneme Gir'}
            icon={<FiActivity size={20} />}
            accentColor="#10b981"
          >
             <p className="text-xs text-muted" style={{ marginTop: 'var(--space-1)', display: 'flex', alignItems: 'center', gap: '4px', color: '#10b981' }}>
                {latestTrial ? (latestTrial.yorumlar?.yayin || 'Detay Gör') : 'Analiz Sayfasına Git'}
             </p>
          </StatCard>
        </div>
      </div>

      {/* Daily Quote */}
      {dailyQuote && (
        <GlassCard accent className="dashboard-quote">
          <p className="text-lg" style={{ fontStyle: 'italic', lineHeight: 1.6 }}>
            "{dailyQuote}"
          </p>
        </GlassCard>
      )}

      {/* Quick Actions */}
      <div className="dashboard-actions">
        <Button variant="secondary" size="lg" onClick={() => navigate('/goals')} icon={<FiTarget size={18} />}>
          Hedeflerim
        </Button>
        <Button variant="secondary" size="lg" onClick={onShareCard} icon={<FiAward size={18} />}>
          Başarı Kartı 📸
        </Button>
      </div>
    </div>
  );
}
